'use client';

import { useState, useCallback, useRef, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PullToRefreshProps {
  children: ReactNode;
  onRefresh: () => Promise<void>;
  isRefreshing?: boolean;
  threshold?: number;
  className?: string;
}

type PullState = 'idle' | 'pulling' | 'triggered' | 'refreshing';

export const PullToRefresh = ({
  children,
  onRefresh,
  isRefreshing: externalIsRefreshing,
  threshold = 80,
  className,
}: PullToRefreshProps) => {
  const [pullState, setPullState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);

  const actualIsRefreshing = externalIsRefreshing ?? isRefreshing;

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Only start pull if we're at the top of the scroll container
    if (containerRef.current?.scrollTop === 0) {
      startY.current = e.touches[0].clientY;
      currentY.current = e.touches[0].clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (actualIsRefreshing) return;

    currentY.current = e.touches[0].clientY;
    const distance = currentY.current - startY.current;

    // Only activate if pulling down and at top
    if (distance > 0 && containerRef.current?.scrollTop === 0) {
      // Apply resistance
      const resistedDistance = Math.min(distance * 0.4, threshold * 1.5);
      setPullDistance(resistedDistance);

      if (resistedDistance >= threshold) {
        setPullState('triggered');
      } else {
        setPullState('pulling');
      }
    }
  }, [actualIsRefreshing, threshold]);

  const handleTouchEnd = useCallback(async () => {
    if (actualIsRefreshing) return;

    if (pullState === 'triggered') {
      setPullState('refreshing');
      setIsRefreshing(true);
      setPullDistance(threshold * 0.6); // Keep some distance while refreshing

      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setPullState('idle');
      }
    } else {
      setPullDistance(0);
      setPullState('idle');
    }

    startY.current = 0;
    currentY.current = 0;
  }, [actualIsRefreshing, pullState, threshold, onRefresh]);

  const progress = Math.min(pullDistance / threshold, 1);

  return (
    <div
      ref={containerRef}
      className={cn('relative overflow-auto', className)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Pull indicator */}
      <div
        className={cn(
          'absolute left-0 right-0 flex items-center justify-center',
          'transition-all duration-200',
          pullState === 'idle' ? 'opacity-0' : 'opacity-100'
        )}
        style={{
          top: -40,
          transform: `translateY(${pullDistance}px)`,
        }}
      >
        <div
          className={cn(
            'flex items-center justify-center',
            'w-10 h-10 rounded-full',
            'bg-white dark:bg-secondary-800',
            'shadow-lg border border-gray-200 dark:border-white/10'
          )}
        >
          <RefreshCw
            className={cn(
              'h-5 w-5 text-primary-500',
              'transition-transform duration-200',
              pullState === 'refreshing' && 'animate-spin'
            )}
            style={{
              transform: pullState !== 'refreshing' ? `rotate(${progress * 360}deg)` : undefined,
            }}
          />
        </div>
      </div>

      {/* Status text */}
      {pullState !== 'idle' && (
        <div
          className="absolute left-0 right-0 text-center text-xs text-gray-500 dark:text-secondary-400"
          style={{
            top: -16,
            transform: `translateY(${pullDistance}px)`,
          }}
        >
          {pullState === 'refreshing'
            ? 'Refreshing...'
            : pullState === 'triggered'
            ? 'Release to refresh'
            : 'Pull to refresh'}
        </div>
      )}

      {/* Content */}
      <div
        style={{
          transform: `translateY(${pullDistance}px)`,
          transition: pullState === 'idle' ? 'transform 0.2s ease' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default PullToRefresh;
